// src/components/templates/templateTokens.js
//
// Design tokens for the Template Gallery.
//
// It re-exports `SP`, `FONT` and `BP` from the Manuscript Structure page's
// tokens rather than restating them — the same thing `corkTokens.js` does — so
// there is one place where the prototype palette was mapped onto the app's
// primary and secondary, and one place it can change. Read the header of
// `structureTokens.js` for that mapping.
//
// Colours are the app's: the gallery prototype's violet accent, plum and
// greys map onto `SP.purple`, `SP.purpleDeep`, `SP.gray` and `SP.lineSoft`, and
// its lavender grounds onto `SP.lavender`. The prototype gave each category its
// own hue once; the app has two colours, so the category is carried by its
// label, which every card, pill and detail page shows.
//
// The book covers are the exception, deliberately — see TemplateCover.jsx.

export { SP, FONT, BP, eyebrowSx } from '../manuscript/structureTokens';

// Display order for the filter row and the grid. Not alphabetical: the two
// categories most authors want are first, and the planning sheets — which are
// reference documents rather than books — are last.
export const TEMPLATE_CATEGORIES = [
  { id: 'fiction', label: 'Fiction' },
  { id: 'nonfiction', label: 'Non-Fiction' },
  { id: 'screenwriting', label: 'Screenwriting' },
  { id: 'stageplay', label: 'Stage' },
  { id: 'comic', label: 'Comics' },
  { id: 'academic', label: 'Academic' },
  { id: 'planning', label: 'Planning Sheets' },
];

export const CATEGORY_LABEL = Object.fromEntries(
  TEMPLATE_CATEGORIES.map((c) => [c.id, c.label])
);

/**
 * The categories present in a catalogue, in display order: the known ones
 * first, then any the frontend has never heard of.
 *
 * The trailing part is the point. The catalogue lives in Mongo precisely so it
 * can be changed without a deploy, which means a category can appear that this
 * file does not list. Filtering to `TEMPLATE_CATEGORIES` alone would drop those
 * templates out of the Create dialog's picker while the gallery grid still
 * showed them under "All templates" — a template you can see and cannot choose.
 * Falling back to the raw id as a label is worse than a nice name and far
 * better than a missing template.
 */
export function orderedCategoriesFor(templates) {
  const present = new Set((templates || []).map((t) => t.category).filter(Boolean));
  const known = TEMPLATE_CATEGORIES.filter((c) => present.has(c.id));
  const knownIds = new Set(TEMPLATE_CATEGORIES.map((c) => c.id));
  const unknown = [...present]
    .filter((id) => !knownIds.has(id))
    .sort()
    .map((id) => ({ id, label: id }));
  return [...known, ...unknown];
}

// The prototype's display face, for the hero and a template's name on its
// detail page. Loaded app-wide in public/index.html.
export const FONT_DISPLAY = '"Playfair Display", Georgia, serif';

// Where the detail page's three-column head drops to two and its metric row
// wraps. The prototype's own line; the app's `BP` tiers (900 and 640) handle
// everything narrower.
export const BP_WIDE = '@media (max-width: 1180px)';
